import { useEffect, useState } from "react";
import {
  AppButton,
  AppDialog,
  FormField,
  toast,
} from "../../components/ui";
import { IconButton, InputAdornment } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { UsersService } from "../../api";

type ChangePasswordDialogProps = {
  open: boolean;
  onClose: () => void;
};

function PasswordField({
  label,
  value,
  onChange,
  show,
  onToggleShow,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggleShow: () => void;
  autoComplete: string;
}) {
  return (
    <FormField
      required
      label={label}
      type={show ? "text" : "password"}
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={onToggleShow}
              edge="end"
              size="small"
            >
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

export default function ChangePasswordDialog({
  open,
  onClose,
}: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setSubmitting(false);
  }, [open]);

  const handleSubmit = async () => {
    const current = currentPassword.trim();
    const next = newPassword.trim();
    const confirm = confirmPassword.trim();

    if (!current || !next || !confirm) {
      toast.error("Please fill in all password fields.");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (current === next) {
      toast.error("New password must be different from your current password.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await UsersService.postApiUsersChangePassword({
        currentPassword: current,
        newPassword: next,
      });

      if (!result.success) {
        toast.error(result.message ?? "Could not change password.");
        return;
      }

      toast.success("Your password was updated.");
      onClose();
    } catch {
      toast.error("Could not change password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Change password"
      contentDividers
      actions={
        <>
          <AppButton
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Save"}
          </AppButton>
          <AppButton
            onClick={onClose}
            variant="outlined"
            disabled={submitting}
          >
            Cancel
          </AppButton>
        </>
      }
    >
      <div className="row mt-2">
        <div className="col-12 mb-3">
          <PasswordField
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            onToggleShow={() => setShowCurrent((v) => !v)}
            autoComplete="current-password"
          />
        </div>
        <div className="col-12 mb-3">
          <PasswordField
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            onToggleShow={() => setShowNew((v) => !v)}
            autoComplete="new-password"
          />
        </div>
        <div className="col-12 mb-3">
          <PasswordField
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showConfirm}
            onToggleShow={() => setShowConfirm((v) => !v)}
            autoComplete="new-password"
          />
        </div>
      </div>
    </AppDialog>
  );
}
